import React from 'react';
import { Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { GO_API, useApi } from '../classes/appApi';
import { useOffline } from '../components/App/AppOfflineContext';
import MainLayout, { useMainLayoutContext } from '../components/MainLayout';
import { AbsenceModel, AbsenceTypeModel, HolidayEntitlementModel } from '../defs/defs';
import EqxCard from '../shared/components/EqxCard/EqxCard';
import EqxError from '../shared/components/EqxError';
import EqxForm, { EqxFormInputDate, EqxFormInputRadioButtons, EqxFormInputSelect, EqxFormInputTextarea, EqxFormInputTime, EqxFormRow, EqxFormSubmit, useFormState } from '../shared/components/EqxForm/EqxForm';
import EqxIcon from '../shared/components/EqxIcon';
import EqxListGroup from '../shared/components/EqxListGroup/EqxListGroup';
import { default as EqxLoading, default as Loading } from '../shared/components/EqxLoading';
import EqxRequestHandler from '../shared/components/EqxRequestHandler/EqxRequestHandler';
import useApiEndpoint from '../shared/hooks/ApiEndpoint';
import { AbsenceListItem } from './PageAbsences';

// AbsenceListItem component (copied from PageAbsences.tsx)
interface AbsenceListItemProps {
    absence: AbsenceModel;
    entitlement: any | null;
}

interface AbsenceRequestFormProps {
    formState: any;
    onSuccess: (response: any) => void;
    currentHolidayEntitlement: HolidayEntitlementModel | null;
    absenceTypeRequest: { loading: boolean, error: string | null, response: AbsenceTypeModel[] | null };
}

const AbsenceRequestForm: React.FC<AbsenceRequestFormProps> = ({ formState, onSuccess, currentHolidayEntitlement, absenceTypeRequest }) => {
    const api = useApi()
    const { isOffline } = useOffline()

    const formData = formState.data

    const mainLayoutContext = useMainLayoutContext()
    const {
        addSuccessMessage: showGlobalSuccess,
        addErrorMessage: showGlobalError,
        addLoadingMessage: showGlobalLoading,
    } = mainLayoutContext.messages

    // Get current holiday entitlement and see if it's in hours
    const isHolidayEntitlementInHours = (currentHolidayEntitlement?.entitlementHours != null)

    // Auto-set finish date to start date if finish date is blank
    React.useEffect(() => {
        if (formData.absenceDateType === 'oneday') {
            formState.setValue('finishDate', formData.startDate)
            if (isHolidayEntitlementInHours) {
                formState.setValue('finishDateIsHalfDay', formData.oneDayAmOrPmHours == 'allday' ? 'false' : (formData.oneDayAmOrPmHours == 'partial' ? 'true' : 'false'))
                formState.setValue('startDateIsHalfDay', formData.oneDayAmOrPmHours == 'allday' ? 'false' : (formData.oneDayAmOrPmHours == 'partial' ? 'true' : 'false'))
            } else {
                formState.setValue('finishDateIsHalfDay', formData.oneDayAmOrPm == 'allday' ? 'false' : (formData.oneDayAmOrPm == 'am' ? 'true' : 'false'))
                formState.setValue('startDateIsHalfDay', formData.oneDayAmOrPm == 'allday' ? 'false' : (formData.oneDayAmOrPm == 'pm' ? 'true' : 'false'))
            }
        } else if (formData.absenceDateType === 'openended') {
            formState.setValue('finishDateIsHalfDay', 'false')
            formState.setValue('finishDate', undefined)
        } else {
            if (isHolidayEntitlementInHours) {
                formState.setValue('finishDateIsPartial', formData.startDateIsPartial)
            }
        }
        if (formData.startDate && !formData.finishDate) {
            formState.setValue('finishDate', formData.startDate)
        }
        if (isHolidayEntitlementInHours) {
            formState.setValue('startDateIsHalfDay', 'false')
            formState.setValue('finishDateIsHalfDay', 'false')
        } else {
            formState.setValue('startDateIsPartial', 'false')
            formState.setValue('finishDateIsPartial', 'false')
            formState.setValue('finishTime', '')
            formState.setValue('startTime', '')
        }
    }, [formData.startDate, formData.absenceDateType, formData.oneDayAmOrPm, isHolidayEntitlementInHours, formData.startDateIsHalfDay, formData.oneDayAmOrPmHours, formData.startDateIsPartial])

    const validateAbsence = (data: any) => {
        if (data.startDate > data.finishDate) {
            return { startDate: 'Start date must be before finish date' }
        }
        if (data.startDate == data.finishDate) {
            if (data.startDateIsHalfDay == 'true' && data.finishDateIsHalfDay == 'true') {
                return { finishDate: 'Cannot be both AM and PM on a one-day absence' }
            }
            if (data.startTime && data.finishTime) {
                const startMinutes = parseInt(data.startTime.replace(':', ''))
                const finishMinutes = parseInt(data.finishTime.replace(':', ''))
                if (startMinutes >= finishMinutes) {
                    return { finishTime: 'Finish time must be after start time' }
                }
            }
        }
        return {}
    }

    const handleFormSuccess = (response: any) => {
        showGlobalSuccess('Absence request submitted successfully')
        onSuccess(response)
    }

    return (
        <EqxCard isSection={true} disabled={isOffline} disabledMessage={<><EqxIcon fa="fas fa-wifi " /> Unavailable Offline</>}>
            <EqxForm
                api={api}
                state={formState}
                action={`${GO_API}/absences/create`}
                onSuccess={handleFormSuccess}
                validate={validateAbsence}
            >
                <EqxFormRow name="absenceTypeID" label="Absence Type">
                    <EqxRequestHandler
                        state={{ loading: absenceTypeRequest.loading, error: absenceTypeRequest.error }}
                        loading={<Loading size="sm" />}
                        error={<span className="text-danger">Error loading absence types</span>}
                        content={() => (
                            <>
                                <EqxFormInputSelect
                                    name="absenceTypeID"
                                    options={absenceTypeRequest.response?.map(t => ({ value: t.id.toString(), label: t.name })) || []}
                                    required={true}
                                />
                            </>
                        )} />
                </EqxFormRow>
                <EqxFormRow name="absenceDateType">
                    <EqxFormInputRadioButtons name="absenceDateType" options={[{ value: 'oneday', label: 'One Day' }, { value: 'manydays', label: 'Many Days' }, { value: 'openended', label: 'Open Ended' }]} required={true} />
                </EqxFormRow>
                {formData.absenceDateType === 'oneday' ? (
                    <>
                        <EqxFormRow name="startDate" label={formData.absenceDateType === 'oneday' ? 'Date' : 'Start Date'}>
                            <EqxFormInputDate name="startDate" required={true} />
                        </EqxFormRow>
                        <EqxFormRow name="oneDayAmOrPmHours">
                            {isHolidayEntitlementInHours ? (
                                <>
                                    <EqxFormInputRadioButtons name="oneDayAmOrPmHours" options={[{ value: 'allday', label: 'All Day' }, { value: 'partial', label: 'Partial Day' }]} required={true} />
                                    {formData.oneDayAmOrPmHours == 'partial' && (
                                        <>
                                            <div className="row mt-2">
                                                <div className="col-6">
                                                    <EqxFormRow name="startTime" label="From">
                                                        <EqxFormInputTime name="startTime" required={true} />
                                                    </EqxFormRow>
                                                </div>
                                                <div className="col-6">
                                                    <EqxFormRow name="finishTime" label="To">
                                                        <EqxFormInputTime name="finishTime" required={true} />
                                                    </EqxFormRow>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </>
                            ) : (
                                <EqxFormInputRadioButtons name="oneDayAmOrPm" options={[{ value: 'am', label: 'AM only' }, { value: 'allday', label: 'All Day' }, { value: 'pm', label: 'PM only' }]} required={true} />
                            )}
                        </EqxFormRow>
                    </>
                ) : (
                    <EqxFormRow name="startDate" label={formData.absenceDateType === 'oneday' ? 'Date' : 'Start Date'}>
                        <div className="row">
                            <div className="col-6">
                                <EqxFormInputDate name="startDate" required={true} />
                                <div className="form-help-text">The first day you will be absent</div>
                            </div>
                            <div className="col-6">
                                {isHolidayEntitlementInHours ? (
                                    <EqxFormInputRadioButtons name="startDateIsPartial" options={[{ value: 'false', label: 'All Day' }, { value: 'true', label: 'Partial Day' }]} required={true} />
                                ) : (
                                    <EqxFormInputRadioButtons name="startDateIsHalfDay" options={[{ value: 'false', label: 'All Day' }, { value: 'true', label: 'PM only' }]} required={true} />
                                )}
                            </div>
                        </div>
                    </EqxFormRow>
                )}
                {isHolidayEntitlementInHours && formData.startDateIsPartial == 'true' && formData.absenceDateType !== 'oneday' && (
                    <EqxFormRow name="startTime" label="From">
                        <EqxFormInputTime name="startTime" required={true} />
                    </EqxFormRow>
                )}
                {formData.absenceDateType === 'manydays' && (
                    <>
                        <EqxFormRow name="finishDate" label="Finish Date">
                            <div className="row">
                                <div className="col-6">
                                    <EqxFormInputDate name="finishDate" required={true} />
                                    <div className="form-help-text">The last day you will be absent</div>
                                </div>
                                <div className="col-6">
                                    {isHolidayEntitlementInHours ? (
                                        <EqxFormInputRadioButtons name="finishDateIsPartial" options={[{ value: 'false', label: 'All Day' }, { value: 'true', label: 'Partial Day' }]} required={true} />
                                    ) : (
                                        <EqxFormInputRadioButtons name="finishDateIsHalfDay" options={[{ value: 'false', label: 'All Day' }, { value: 'true', label: 'AM only' }]} required={true} />
                                    )}
                                </div>
                            </div>
                        </EqxFormRow>
                        {isHolidayEntitlementInHours && formData.finishDateIsPartial == 'true' && (
                            <EqxFormRow name="finishTime" label="To">
                                <EqxFormInputTime name="finishTime" required={true} />
                            </EqxFormRow>
                        )}
                    </>
                )}
                {formData.absenceDateType === 'openended' && (
                    <Alert variant="warning" className="mt-3">
                        This absence will be open ended until the finish date is set. Mainly used for Sick Leave
                    </Alert>)}
                <EqxFormRow name="notes" label="Notes">
                    <EqxFormInputTextarea name="notes" />
                    <div className="form-help-text">
                        Please remember that notes entered here are visible to others.
                    </div>
                </EqxFormRow>
                <EqxFormSubmit label="Request Absence" className="w-100" size="lg" />
            </EqxForm>
        </EqxCard>
    );
};

// Main content component extracted from PageAbsencesCreate
const AbsenceCreateContent: React.FC = () => {
    const navigate = useNavigate()
    const api = useApi()
    const { error: holidayEntitlementsError, loading: holidayEntitlementsLoading, response: holidayEntitlements } = useApiEndpoint<HolidayEntitlementModel[]>(api, api.endpoints.holidayEntitlements())
    const absenceTypeRequest = useApiEndpoint<AbsenceTypeModel[]>(api, api.endpoints.absenceTypes())
    const { error: absenceTypesError, loading: absenceTypesLoading, response: absenceTypes } = absenceTypeRequest

    const formState = useFormState(undefined, {
        data: {
            absenceDateType: 'oneday',
            startDateIsHalfDay: 'false',
            finishDateIsHalfDay: 'false',
            startDateIsPartial: 'false',
            finishDateIsPartial: 'false',
            oneDayAmOrPm: 'allday',
            oneDayAmOrPmHours: 'allday',
        }
    })

    // Check allowed - moved after all hooks to follow Rules of Hooks
    const mainContext = useMainLayoutContext()
    if (!mainContext.permissions?.canBookAbsence) {
        return <EqxError error="You do not have permission to book an absence." />
    }
    if (!mainContext.user?.staffID) {
        if (!mainContext.user) {
            return <EqxLoading />
        } else {
            return <EqxError error="Only staff can request absences" />;
        }
    }

    // Build preview absence object
    let previewAbsence = null
    if (formState.isValid() && formState.touched.length > 0) {
        const formData = formState.data
        const absence: AbsenceModel = {
            id: 0, // temporary ID for preview
            notes: formData.notes || '',
            startDate: formData.startDate,
            startDateIsHalfDay: formData.startDateIsHalfDay == 'true' ? true : false,
            finishDate: formData.finishDate,
            finishDateIsHalfDay: formData.finishDateIsHalfDay == 'true' ? true : false,
            workingDays: undefined, // would need calculation
            workingHours: undefined, // would need calculation
            absenceTypeID: parseInt(formData.absenceTypeID),
            absenceType: absenceTypes?.find(at => at.id == parseInt(formData.absenceTypeID)) || undefined,
            approved: null, // pending approval
            staffID: 0, // would be set by server
            createdTimestamp: new Date().toISOString()
        }
        previewAbsence = absence
    }

    const handleFormSuccess = (response: any) => {
        navigate('/absences')
    }

    // Get current holiday entitlement
    const currentHolidayEntitlement = holidayEntitlements?.find(he => he.isCurrent == true) || null

    return (
        <>
            <EqxRequestHandler
                state={{ loading: holidayEntitlementsLoading || absenceTypesLoading, error: holidayEntitlementsError || absenceTypesError }}
                content={() => (
                    <AbsenceRequestForm
                        formState={formState}
                        onSuccess={handleFormSuccess}
                        currentHolidayEntitlement={currentHolidayEntitlement}
                        absenceTypeRequest={absenceTypeRequest}
                    />
                )}
            />
            {
                previewAbsence && (
                    <EqxCard isSection={true} title="Preview" collapsible={false} noPadding={true}>
                        <EqxListGroup isChild={true}>
                            <AbsenceListItem absence={previewAbsence} entitlement={null} />
                        </EqxListGroup>
                    </EqxCard>
                )
            }
        </>
    );
};

const PageAbsencesCreate: React.FC = () => {
    return (
        <MainLayout pageTitle="Request Absence" relatedLinks={[{ text: 'Absences', url: '/absences' }]}>
            <AbsenceCreateContent />
        </MainLayout>
    );
};

export default PageAbsencesCreate; 